import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../../lib/supabase';
import { ArrowLeft, Shield, User, Phone, Mail, Lock, IdCard, Loader2, CheckCircle2, AlertCircle, Users } from 'lucide-react';
interface AdminForm {
  full_name: string;
  email: string;
  temporary_password: string;
  phone: string;
  national_id: string;
  role: string;
  is_active: boolean;
}
interface AdminResult {
  user_id?: string;
  email?: string;
  full_name?: string;
  role?: string;
  is_active?: boolean;
}
const ROLES = [{
  value: 'super_admin',
  label: 'Super Admin',
  desc: 'Acceso total'
}, {
  value: 'platform_admin',
  label: 'Admin de Plataforma',
  desc: 'Administración general'
}, {
  value: 'support',
  label: 'Soporte',
  desc: 'Acceso operativo'
}];
const inputClass = 'w-full bg-[#141414] border border-white/10 rounded-xl pl-12 pr-4 py-4 text-base text-white placeholder:text-white/15 focus:outline-none focus:border-[#006847] focus:ring-1 focus:ring-[#006847]';
const labelClass = 'block text-xs font-bold text-white/40 uppercase tracking-widest mb-2';
const iconClass = 'absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/20';
export function EnrollmentPlatformAdminPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<AdminForm>({
    full_name: '',
    email: '',
    temporary_password: '',
    phone: '',
    national_id: '',
    role: 'platform_admin',
    is_active: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AdminResult | null>(null);
  const updateField = (field: keyof AdminForm, value: string | boolean) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    if (!form.full_name.trim()) return setError('El nombre es obligatorio.');
    if (!form.email.trim()) return setError('El correo es obligatorio.');
    if (!form.temporary_password) return setError('La contraseña es obligatoria.');
    if (form.temporary_password.length < 8) return setError('La contraseña debe tener al menos 8 caracteres.');
    if (!form.role) return setError('El rol es obligatorio.');
    setIsSubmitting(true);
    const payload = {
      fullName: form.full_name.trim(),
      email: form.email.trim().toLowerCase(),
      password: form.temporary_password,
      phone: form.phone.trim() || null,
      national_id: form.national_id.trim() || null,
      role: form.role,
      isActive: form.is_active
    };
    console.log('[EnrollmentPlatformAdmin] payload keys:', Object.keys(payload));
    // Get user session for Authorization
    const {
      data: sessionData
    } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;
    console.log('[EnrollmentPlatformAdmin] Session before submit:', {
      hasSession: !!sessionData?.session,
      accessToken: accessToken ? 'present' : 'MISSING',
      userId: sessionData?.session?.user?.id ?? null
    });
    if (!accessToken) {
      setError('No hay sesión activa. Inicia sesión primero en /admin/login');
      setIsSubmitting(false);
      return;
    }
    let data: any = null;
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/create-platform-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          apikey: SUPABASE_ANON_KEY
        },
        body: JSON.stringify(payload)
      });
      data = await res.json();
      console.log('response:', {
        status: res.status,
        data
      });
      if (!res.ok) {
        console.error('backend error:', data);
        const raw = (data?.error || data?.details || data?.message || '').toLowerCase();
        let friendlyMsg = data?.error || data?.details || data?.message || `Error del servidor (${res.status})`;
        if (raw.includes('email') || raw.includes('correo') || raw.includes('already registered') || raw.includes('already been registered')) {
          friendlyMsg = `Ya existe un usuario con el correo "${form.email}".`;
        } else if (raw.includes('national_id') || raw.includes('cédula')) {
          friendlyMsg = `Ya existe un perfil con la cédula "${form.national_id}".`;
        }
        setError(friendlyMsg);
        setIsSubmitting(false);
        return;
      }
    } catch (fetchErr: any) {
      console.error('fetch error:', fetchErr);
      setError(fetchErr.message || 'Error de conexión con el servidor.');
      setIsSubmitting(false);
      return;
    }
    setIsSubmitting(false);
    if (!data?.success) {
      setError(data?.error || data?.details || 'Error al crear el admin.');
      return;
    }
    setResult({
      user_id: data.admin_user?.user_id,
      email: data.admin_user?.email || form.email,
      full_name: data.admin_user?.full_name || form.full_name,
      role: data.admin_user?.role || form.role,
      is_active: data.admin_user?.is_active ?? form.is_active
    });
  };
  const getRoleLabel = (value: string) => ROLES.find(r => r.value === value)?.label || value;
  if (result) {
    return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans flex flex-col items-center justify-center p-6" data-id="element-4239" data-mp="3km5znosjjdbp">
        <div className="max-w-md w-full space-y-6 text-center" data-id="element-4240" data-mp="2gji32vp154n">
          <div className="w-20 h-20 bg-[#006847]/20 rounded-full flex items-center justify-center mx-auto" data-id="element-4241" data-mp="3tnceltfjkg2u">
            <CheckCircle2 className="w-10 h-10 text-[#00a870]" data-id="element-4242" data-mp="249qykfn1lss4" />
          </div>
          <h1 className="text-2xl font-bold" data-id="element-4243" data-mp="1zaea5qgiwdnf">Admin de NOIZA creado</h1>
          <p className="text-white/50" data-id="element-4244" data-mp="36ga14aw8xsm">
            La cuenta de administrador fue creada exitosamente.
          </p>

          <div className="bg-[#141414] border border-white/10 rounded-2xl p-5 text-left space-y-3 text-sm" data-id="element-4245" data-mp="8519v2m8hfgj">
            <div className="flex justify-between" data-id="element-4246" data-mp="c6v8748yi7dj">
              <span className="text-white/50" data-id="element-4247" data-mp="c9941fbgnnm5">Nombre</span>
              <strong data-id="element-4248" data-mp="h7aifmxxyyra">{result.full_name}</strong>
            </div>
            <div className="flex justify-between" data-id="element-4249" data-mp="m5kfk33889fj">
              <span className="text-white/50" data-id="element-4250" data-mp="m4suvnb63w41">Email</span>
              <strong className="font-mono text-xs" data-id="element-4251" data-mp="r3vomblp6fwp">{result.email}</strong>
            </div>
            <div className="flex justify-between" data-id="element-4252" data-mp="vvdthaqud0my">
              <span className="text-white/50" data-id="element-4253" data-mp="vvxamp8o3kzx">Rol</span>
              <span className="inline-flex items-center gap-1.5 bg-[#006847]/20 text-[#00a870] text-xs font-bold px-2.5 py-0.5 rounded-lg" data-id="element-4254" data-mp="zx8q41xtupue">
                <Shield className="w-3 h-3" data-id="element-4255" data-mp="14xd6zt2ndml5" />
                {getRoleLabel(result.role || '')}
              </span>
            </div>
            <div className="flex justify-between" data-id="element-4256" data-mp="1jyttozkswah4">
              <span className="text-white/50" data-id="element-4257" data-mp="1jzms9d9lvto6">Estado</span>
              <strong className={result.is_active ? 'text-[#00a870]' : 'text-red-400'} data-id="element-4258" data-mp="1f00f13ts2dfd">
                {result.is_active ? 'Activo' : 'Inactivo'}
              </strong>
            </div>
            <div className="flex justify-between" data-id="element-4259" data-mp="15zu7dykrg7o4">
              <span className="text-white/50" data-id="element-4260" data-mp="16156hl39gr8w">Contraseña temporal</span>
              <strong className="font-mono text-xs" data-id="element-4261" data-mp="22otf9chfn7fs">
                {form.temporary_password}
              </strong>
            </div>
            {result.user_id && <div className="flex justify-between" data-id="element-4262" data-mp="1syqdonw8wl5u">
                <span className="text-white/50" data-id="element-4263" data-mp="1sxf8cjn5j5cm">User ID</span>
                <strong className="font-mono text-[10px] text-white/60 truncate max-w-[200px]" data-id="element-4264" data-mp="1ox54pdmu0r0x">
                  {result.user_id}
                </strong>
              </div>}
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 text-left" data-id="element-4265" data-mp="2goay7b81rqh8">
            <p className="text-sm text-yellow-300" data-id="element-4266" data-mp="30ccx07uvdnh8">
              ⚠️ Comparte las credenciales de forma segura. La contraseña
              temporal debe cambiarse en el primer inicio de sesión.
            </p>
          </div>

          <div className="flex gap-3 pt-4" data-id="element-4267" data-mp="2qcuezwyisbrt">
            <button onClick={() => {
            setResult(null);
            setForm({
              full_name: '',
              email: '',
              temporary_password: '',
              phone: '',
              national_id: '',
              role: 'platform_admin',
              is_active: true
            });
          }} className="flex-1 py-3 bg-white/5 border border-white/10 rounded-xl font-bold text-white/70 hover:bg-white/10 transition-colors" data-id="element-4268" data-mp="2vdibuz9fjmty">
              Crear otro admin
            </button>
            <button onClick={() => navigate('/admin/users')} className="flex-1 py-3 bg-[#006847] rounded-xl font-bold text-white hover:bg-[#005a3d] transition-colors" data-id="element-4269" data-mp="22pw37clchs08">
              Ver usuarios
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans" data-id="element-4270" data-mp="trefax0w1vmi">
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-8 space-y-6" data-id="element-4271" data-mp="pr3z6rjdtotx">
        {/* Header */}
        <div className="flex items-center gap-4" data-id="element-4272" data-mp="21063e3tl7h6">
          <button onClick={() => navigate('/admin/enrollment')} className="w-10 h-10 bg-[#141414] border border-white/10 rounded-xl flex items-center justify-center hover:bg-white/5 transition-colors" data-id="element-4273" data-mp="21rplqk57er3">
            <ArrowLeft className="w-5 h-5" data-id="element-4274" data-mp="62v68fa0letd" />
          </button>
          <div data-id="element-4275" data-mp="61acuszfs1x3">
            <h1 className="text-2xl font-bold" data-id="element-4276" data-mp="60qrf2akuveb">Admin de NOIZA</h1>
            <p className="text-white/40 text-sm" data-id="element-4277" data-mp="3ehc5gxi2geoa">
              Crea usuarios internos de NOIZA con permisos de plataforma.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-4278" data-mp="ocg5zobov6tj">
          <div className="space-y-4" data-id="element-4279" data-mp="o9ty1nkvypas">
            <div className="flex items-center gap-2 pb-1 border-b border-white/5" data-id="element-4280" data-mp="jcbopw2yi829">
              <Shield className="w-4 h-4 text-[#00a870]" data-id="element-4281" data-mp="fb8b7g8dso47" />
              <h2 className="text-sm font-bold text-white/60 uppercase tracking-widest" data-id="element-4282" data-mp="f9ou5newi91d">
                Datos del admin
              </h2>
            </div>

            {/* Name */}
            <div data-id="element-4283" data-mp="5du4w9g69fpm">
              <label className={labelClass} data-id="element-4284" data-mp="5ed9ypkq49m8">Nombre completo</label>
              <div className="relative" data-id="element-4285" data-mp="laqxbdkoika">
                <User className={iconClass} data-id="element-4286" data-mp="k92843mvxk3" />
                <input type="text" value={form.full_name} onChange={e => updateField('full_name', e.target.value)} placeholder="Juan Pérez" className={inputClass} required data-id="element-4287" data-mp="3rrvcwgort0ag" />
              </div>
            </div>

            {/* Email */}
            <div data-id="element-4288" data-mp="3dv851iva4ppr">
              <label className={labelClass} data-id="element-4289" data-mp="1tb39j0ckwfvl">Correo electrónico</label>
              <div className="relative" data-id="element-4290" data-mp="1tcxjxkq84plf">
                <Mail className={iconClass} data-id="element-4291" data-mp="1y9eht6n86ibp" />
                <input type="email" value={form.email} onChange={e => updateField('email', e.target.value)} placeholder="admin@noiza.com" className={inputClass} required data-id="element-4292" data-mp="1y8370mvd4ab3" />
              </div>
            </div>

            {/* Password */}
            <div data-id="element-4293" data-mp="1fkhpv9faw1wn">
              <label className={labelClass} data-id="element-4294" data-mp="1fjy6rpdwz7h1">Contraseña temporal</label>
              <div className="relative" data-id="element-4295" data-mp="1jkrtwgjj8i8v">
                <Lock className={iconClass} data-id="element-4296" data-mp="1jjqk0fe961sq" />
                <input type="text" value={form.temporary_password} onChange={e => updateField('temporary_password', e.target.value)} placeholder="Mínimo 8 caracteres" className={`${inputClass} font-mono`} required minLength={8} data-id="element-4297" data-mp="1oc9wta548swh" />
              </div>
            </div>

            {/* Phone */}
            <div data-id="element-4298" data-mp="1u5fgjb0e7w4i">
              <label className={labelClass} data-id="element-4299" data-mp="28vwhkt948k0v">
                Teléfono{' '}
                <span className="text-white/20 normal-case" data-id="element-4300" data-mp="28xz77c6cwb52">(opcional)</span>
              </label>
              <div className="relative" data-id="element-4301" data-mp="23zflncvod1d8">
                <Phone className={iconClass} data-id="element-4302" data-mp="1g6pdleqw7288" />
                <input type="tel" value={form.phone} onChange={e => updateField('phone', e.target.value)} placeholder="88888888" className={inputClass} data-id="element-4303" data-mp="1g9lb51ytvow5" />
              </div>
            </div>

            {/* National ID */}
            <div data-id="element-4304" data-mp="30n2fr45a07ni">
              <label className={labelClass} data-id="element-4305" data-mp="30mkgrzr8bfbw">
                Cédula{' '}
                <span className="text-white/20 normal-case" data-id="element-4306" data-mp="2wm0md3y1iptg">(opcional)</span>
              </label>
              <div className="relative" data-id="element-4307" data-mp="345jd5wdizncl">
                <IdCard className={iconClass} data-id="element-4308" data-mp="344gntsvupsys" />
                <input type="text" value={form.national_id} onChange={e => updateField('national_id', e.target.value)} placeholder="Ej: 304590658" className={inputClass} data-id="element-4309" data-mp="394cszvgabqfx" />
              </div>
            </div>

            {/* Role */}
            <div data-id="element-4310" data-mp="3mwsnu8z18i5r">
              <label className={labelClass} data-id="element-4311" data-mp="3mw9fnjz6p84x">Rol de plataforma</label>
              <div className="relative" data-id="element-4312" data-mp="3rv3xojpbusnz">
                <Shield className={iconClass} data-id="element-4313" data-mp="3rwx5417rlgx1" />
                <select value={form.role} onChange={e => updateField('role', e.target.value)} className={`${inputClass} appearance-none cursor-pointer`} required data-id="element-4314" data-mp="3d6hg0bc89otl">
                  {ROLES.map(r => <option key={r.value} value={r.value} className="bg-[#141414] text-white" data-id="element-4315" data-mp="eghe76dc58l0">
                      {r.label}
                    </option>)}
                </select>
              </div>
              <div className="mt-2 bg-white/5 border border-white/5 rounded-lg p-3 space-y-1" data-id="element-4316" data-mp="2cy19kvgmqorq">
                {ROLES.map(r => <div key={r.value} className="flex items-center gap-2 text-xs" data-id="element-4317" data-mp="281av2ckh418f">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${form.role === r.value ? 'bg-[#00a870]' : 'bg-white/20'}`} data-id="element-4318" data-mp="30oov4jjgxcwr" />
                    <span className={form.role === r.value ? 'text-white font-bold' : 'text-white/40'} data-id="element-4319" data-mp="2vvlxo0bkljba">
                      {r.label}:
                    </span>
                    <span className="text-white/30" data-id="element-4320" data-mp="3jlg8kccbwxsw">{r.desc}</span>
                  </div>)}
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between bg-[#141414] border border-white/10 rounded-xl p-4" data-id="element-4321" data-mp="3vxsfeosqgq3f">
              <span className="text-sm font-medium text-white/70" data-id="element-4322" data-mp="4tkalufgyep9">
                Usuario activo
              </span>
              <button type="button" onClick={() => updateField('is_active', !form.is_active)} className={`w-14 h-8 rounded-full transition-colors relative ${form.is_active ? 'bg-[#006847]' : 'bg-white/10'}`} data-id="element-4323" data-mp="epn9mrf0xx7z">
                <div className={`w-6 h-6 bg-white rounded-full absolute top-1 transition-transform ${form.is_active ? 'translate-x-7' : 'translate-x-1'}`} data-id="element-4324" data-mp="njs5xqcxg0uw" />
              </button>
            </div>
          </div>

          {/* Error */}
          {error && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3 text-red-400" data-id="element-4325" data-mp="12cvex9cgd6a0">
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-4326" data-mp="7rr8e25y5z29" />
              <div className="text-sm whitespace-pre-line" data-id="element-4327" data-mp="7taljjwb3tzt">{error}</div>
            </div>}

          {/* Submit */}
          <button type="submit" disabled={isSubmitting} className="w-full py-5 bg-[#006847] hover:bg-[#005a3d] disabled:bg-white/5 disabled:text-white/30 text-white font-black text-lg rounded-xl transition-all flex items-center justify-center gap-2 active:scale-95" data-id="element-4328" data-mp="mignhfmj3ybe">
            {isSubmitting ? <>
                <Loader2 className="w-6 h-6 animate-spin" data-id="element-4329" data-mp="15ezn9ebg09va" />
                Creando admin...
              </> : <>
                <Shield className="w-5 h-5" data-id="element-4330" data-mp="376l3vrxsrh0y" />
                Crear Admin de NOIZA
              </>}
          </button>
        </form>
      </div>
    </div>;
}